import type { GetSlashTariff } from './get-slash';
import type { GetInternetDataTariff } from './internet-card-view-data';

export type CombinedInternetTariff = Omit<GetInternetDataTariff, 'text'> & {
  text?: GetInternetDataTariff['text'] | string;
  getSlash: GetSlashTariff;
};

type FallbackImages = Pick<
  GetInternetDataTariff,
  'imageListing' | 'imageHero' | 'imageHeroMd'
>;

/**
 * Overlays the localized Contentful content onto each live GetSlash tariff.
 *
 * For every live tariff we look for a Contentful entry that lists its ID (a
 * single entry can list several IDs, so multiple live tariffs can share the
 * same localized content). When no entry matches, we fall back to the GetSlash
 * tariff's own single-language name/description.
 */
export const combineTariffs = (
  getSlashTariffs: GetSlashTariff[],
  contentfulTariffs: GetInternetDataTariff[],
  fallbackImages?: FallbackImages,
): CombinedInternetTariff[] => {
  return getSlashTariffs
    .map((tariff): CombinedInternetTariff | undefined => {
      const contentfulTariff = contentfulTariffs.find(contentfulTariff => {
        return contentfulTariff.externalId === tariff.id;
      });

      if (contentfulTariff) {
        return {
          ...contentfulTariff,
          getSlash: tariff,
        };
      }

      return {
        ...fallbackImages,
        externalId: tariff.id,
        title: tariff.name,
        text: tariff.description,
        teaser: tariff.description,
        getSlash: tariff,
      };
    })
    .filter((combined): combined is CombinedInternetTariff => {
      if (typeof combined === 'undefined') {
        return false;
      }

      return typeof combined.getSlash !== 'undefined';
    });
};
